/**
 * Loading state component for CCMS operations
 */

import { forwardRef } from 'react';
import { Stack, Text, Loader } from '@/components/ui';
import type { GradientVariant } from '../../types';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

export interface CCMSLoadingStateProps {
  message?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  gradientVariant?: GradientVariant;
  fullScreen?: boolean;
  className?: string;
}

/**
 * Loading state component for displaying loading indicators
 *
 * @example
 * ```tsx
 * <CCMSLoadingState message="Loading files..." />
 * <CCMSLoadingState size="lg" fullScreen />
 * ```
 */
export const CCMSLoadingState = forwardRef<HTMLDivElement, CCMSLoadingStateProps>(
  (props, ref) => {
    const {
      message = 'Loading...',
      size = 'md',
      gradientVariant = 'primary',
      fullScreen = false,
      className,
    } = props;

    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const content = (
      <Stack gap="md" align="center" ref={ref} className={className}>
        <Loader loaderVariant="gradient" gradientVariant={gradientVariant} size={size} />
        {message && (
          <Text size="sm" c="dimmed" style={accessibilityStyles}>
            {message}
          </Text>
        )}
      </Stack>
    );

    if (fullScreen) {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            width: '100%',
          }}
        >
          {content}
        </div>
      );
    }

    return content;
  }
);

CCMSLoadingState.displayName = 'CCMSLoadingState';

export default CCMSLoadingState;
